import { OutlinedInput, outlinedInputClasses } from '@mui/material'
import { forwardRef } from 'react'
import { RxExclamationTriangle } from 'react-icons/rx'
import { TbExclamationCircle } from 'react-icons/tb'
import { tss } from 'tss-react/mui'
import { uiColors } from '~/common/styles/colors'
import {
  RecordCellEditOrigin,
  useEditableTableCellOriginContext,
} from '~/components/table-cells/useEditableTableCellOriginProvider'
import {
  getInputColorPrimitives,
  getLongTextFieldPrimitives,
  inputBasePrimitives,
  inputErrorPrimitives,
} from '~/components/ui/atoms/input/variants/primitives'
import { InputProps } from './types'

function InputEndAdornment({ variant, error, disabled }: InputProps) {
  if (disabled) {
    return null
  }

  if (error) {
    return <TbExclamationCircle color={uiColors.content.danger} size="16px" />
  }

  if (variant === 'warning') {
    return <RxExclamationTriangle color={uiColors.content.warning} size="16px" />
  }

  return null
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'primary',
      error,
      disabled,
      endAdornment,
      multiline,
      longTextField,
      type,
      className,
      maxRows,
      isBorderless,
      ...rest
    },
    ref,
  ) => {
    const origin = useEditableTableCellOriginContext()
    const { classes, cx } = useStyles({
      variant,
      error,
      longTextField,
      type,
      isBorderless,
      isInEditableTableCellForm: origin === RecordCellEditOrigin.TABLE_DETAILS,
    })

    return (
      <OutlinedInput
        ref={ref}
        className={cx(classes.root, className)}
        disabled={disabled}
        endAdornment={
          <>
            <InputEndAdornment disabled={disabled} error={error} variant={variant} />
            {endAdornment}
          </>
        }
        error={error}
        // maxRows cannot be used with rows prop
        maxRows={rest.rows ? undefined : (maxRows ?? 6)}
        multiline={multiline}
        onWheel={(event) => {
          if (type === 'number') {
            event.target instanceof HTMLElement && event.target.blur()
          }
        }}
        type={type}
        {...rest}
      />
    )
  },
)

Input.displayName = 'Input'

const useStyles = tss
  .withParams<
    Pick<InputProps, 'variant' | 'error' | 'type'> & {
      isInEditableTableCellForm: boolean
      longTextField?: boolean
      isBorderless?: boolean
    }
  >()
  .create(
    ({
      variant = 'primary',
      error,
      type,
      isInEditableTableCellForm,
      longTextField,
      isBorderless,
      theme,
    }) => {
      return {
        root: {
          ...inputBasePrimitives,
          ...{
            minHeight: '40px',
            paddingBlock: theme.spacing(1),
            paddingInline: theme.spacing(1.5),
          },
          ...getLongTextFieldPrimitives(longTextField, theme),
          ...getInputColorPrimitives({ variant }),
          ...(error && inputErrorPrimitives),
          [`& .${outlinedInputClasses.input}`]: {
            padding: '0px',
            ...(type === 'number' && isInEditableTableCellForm
              ? { textAlign: 'right', paddingInlineEnd: '4px' }
              : {}),
          },
          ...(isBorderless && {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              border: 'none !important',
            },
          }),
        },
      }
    },
  )
